<template>
  <div class="rank-card-wrapper">
    <div class="rank-header">
      <div class="header-top">
        <i class="el-icon-trophy trophy-icon"></i>
        <span class="header-title">文章排行榜</span>
      </div>
      <el-radio-group v-model="rankType" size="mini" @change="changeRankType" class="rank-tabs">
        <el-radio-button label="hot">
          <i class="el-icon-fire"></i> 热度
        </el-radio-button>
        <el-radio-button label="praise">
          <i class="el-icon-star-on"></i> 点赞
        </el-radio-button>
        <el-radio-button label="comment">
          <i class="el-icon-chat-dot-round"></i> 评论
        </el-radio-button>
      </el-radio-group>
    </div>
    
    <div class="rank-list" v-loading="loading">
      <transition-group name="list">
        <div
          v-for="(item, index) in rankList"
          :key="item.docDigest"
          class="rank-item"
          :class="getRankClass(index)"
          @click="goDetail(item.docDigest)"
        >
          <div class="rank-index">
            <span v-if="index < 3" class="medal" :class="'medal-' + index">
              <i v-if="index === 0" class="el-icon-trophy"></i>
              <i v-else-if="index === 1" class="el-icon-star-on"></i>
              <i v-else class="el-icon-star-off"></i>
            </span>
            <span v-else class="normal-num">{{ index + 1 }}</span>
          </div>
          <div class="rank-content">
            <div class="rank-title" :title="item.title">{{ item.title }}</div>
            <div class="rank-footer-info">
              <div class="author-info">
                <el-avatar :size="20" icon="el-icon-user"></el-avatar>
                <span>{{ item.createUserName || item.author }}</span>
              </div>
              <div class="stats">
                <span class="stat-item" :class="rankType">
                  <i :class="getRankIcon(rankType)"></i>
                  {{ getRankValue(item, rankType) }}
                </span>
                <span class="stat-item view-count">
                  <i class="el-icon-view"></i>
                  {{ item.viewCount || 0 }}
                </span>
                <span class="stat-item time-label">
                  <i class="el-icon-time"></i>
                  {{ formatTime(item.createTime) }}
                </span>
              </div>
            </div>
          </div>
          <div class="rank-trend" v-if="index < 3">
            <i class="el-icon-top" :class="'trend-' + index"></i>
          </div>
        </div>
      </transition-group>
      
      <el-empty v-if="rankList.length === 0 && !loading" description="暂无数据" :image-size="80"></el-empty>
    </div>
    
    <div v-if="rankList.length > 0" class="rank-footer">
      <el-button size="small" @click="refreshRank" round>
        <i class="el-icon-refresh"></i> 换一批
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DocRankList',
  props: {
    categoryId: {
      type: Number,
      default: 1
    },
    limit: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      rankList: [],
      rankType: 'hot',
      loading: false
    };
  },
  created() {
    this.getRankList();
  },
  methods: {
    getRankClass(index) {
      const classes = ['first', 'second', 'third'];
      return classes[index] || '';
    },
    getRankIcon(type) {
      const icons = {
        hot: 'el-icon-fire',
        praise: 'el-icon-star-on',
        comment: 'el-icon-chat-dot-round'
      };
      return icons[type] || 'el-icon-fire';
    },
    getRankValue(item, type) {
      const values = {
        hot: item.hotValue,
        praise: item.praiseCount,
        comment: item.commentCount
      };
      return values[type] || item.hotValue;
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      const now = new Date();
      const diff = now - date;
      
      const minute = 60 * 1000;
      const hour = 60 * minute;
      const dayMs = 24 * hour;
      
      if (diff < minute) return '刚刚';
      if (diff < hour) return Math.floor(diff / minute) + '分钟前';
      if (diff < dayMs) return Math.floor(diff / hour) + '小时前';
      if (diff < 7 * dayMs) return Math.floor(diff / dayMs) + '天前';
      
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}-${day}`;
    },
    changeRankType() {
      this.getRankList();
    },
    async getRankList() {
      this.loading = true;
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/top/getDocTopByCategory',
          data: {
            categoryId: this.categoryId
          }
        });
        if (response.code === '200') {
          this.rankList = response.data || [];
          if (this.rankType === 'praise') {
            this.rankList.sort((a, b) => b.praiseCount - a.praiseCount);
          } else if (this.rankType === 'comment') {
            this.rankList.sort((a, b) => b.commentCount - a.commentCount);
          } else {
            this.rankList.sort((a, b) => b.hotValue - a.hotValue);
          }
          this.rankList = this.rankList.slice(0, this.limit);
        } else {
          this.$message.warning(response.msg || '获取排行榜失败');
        }
      } catch (error) {
        console.error('获取排行榜失败:', error);
        this.$message.error('获取排行榜失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },
    refreshRank() {
      this.getRankList();
    },
    goDetail(docDigest) {
      this.$router.push({ name: 'Content', params: { id: docDigest } });
    }
  }
};
</script>

<style lang="less" scoped>
.rank-card-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
}

.rank-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;

  .header-top {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    .trophy-icon {
      font-size: 22px;
      color: #ffd700;
      filter: drop-shadow(0 2px 4px rgba(255, 215, 0, 0.4));
    }

    .header-title {
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 1px;
    }
  }

  ::v-deep .el-radio-group.rank-tabs {
    display: flex;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    padding: 4px;

    .el-radio-button__inner {
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.8);
      font-size: 12px;
      padding: 6px 16px;
      border-radius: 18px;
      transition: all 0.3s;
      font-weight: 500;

      i {
        margin-right: 4px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.2);
        color: #fff;
      }
    }

    .el-radio-button__orig-radio:checked + .el-radio-button__inner {
      background: #fff;
      color: #667eea;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      font-weight: 600;
    }

    .el-radio-button:last-child .el-radio-button__inner {
      padding-right: 16px;
    }

    .el-radio-button:first-child .el-radio-button__inner {
      padding-left: 16px;
    }
  }
}

.rank-list {
  padding: 12px 16px;
  min-height: 320px;

  .rank-item {
    display: flex;
    align-items: center;
    padding: 14px 12px;
    margin-bottom: 8px;
    background: #fafafa;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;

    &:hover {
      background: #fff;
      border-color: rgba(102, 126, 234, 0.3);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
      transform: translateX(4px);
    }

    &.first {
      background: linear-gradient(135deg, #fff9e6 0%, #fff 100%);
      border-color: rgba(255, 215, 0, 0.3);

      &:hover {
        border-color: #ffd700;
      }
    }

    &.second {
      background: linear-gradient(135deg, #f5f5f5 0%, #fff 100%);
      border-color: rgba(192, 192, 192, 0.3);

      &:hover {
        border-color: #c0c0c0;
      }
    }

    &.third {
      background: linear-gradient(135deg, #fff5eb 0%, #fff 100%);
      border-color: rgba(205, 127, 50, 0.3);

      &:hover {
        border-color: #cd7f32;
      }
    }

    .rank-index {
      flex-shrink: 0;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;

      .medal {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-size: 16px;

        &.medal-0 {
          background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
          color: #fff;
          box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
          font-size: 18px;
        }

        &.medal-1 {
          background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
          color: #fff;
          box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
        }

        &.medal-2 {
          background: linear-gradient(135deg, #cd7f32 0%, #e0a068 100%);
          color: #fff;
          box-shadow: 0 2px 8px rgba(205, 127, 50, 0.4);
        }
      }

      .normal-num {
        font-size: 16px;
        font-weight: 600;
        color: #999;
      }
    }

    .rank-content {
      flex: 1;
      min-width: 0;

      .rank-title {
        font-size: 14px;
        color: #333;
        line-height: 1.5;
        margin-bottom: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        font-weight: 500;
        transition: color 0.3s;

        &:hover {
          color: #667eea;
        }
      }

      .rank-footer-info {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .author-info {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #999;

          ::v-deep .el-avatar {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          }
        }

        .stats {
          display: flex;
          gap: 6px;
          align-items: center;

          .stat-item {
            display: flex;
            align-items: center;
            gap: 3px;
            font-size: 11px;
            font-weight: 500;
            padding: 3px 6px;
            border-radius: 10px;
            background: rgba(0, 0, 0, 0.04);
            white-space: nowrap;

            i {
              font-size: 12px;
            }

            &.hot {
              color: #ff6b6b;
              background: rgba(255, 107, 107, 0.1);

              i {
                color: #ff6b6b;
              }
            }

            &.praise {
              color: #f59e0b;
              background: rgba(245, 158, 11, 0.1);

              i {
                color: #f59e0b;
              }
            }

            &.comment {
              color: #3b82f6;
              background: rgba(59, 130, 246, 0.1);

              i {
                color: #3b82f6;
              }
            }

            &.view-count {
              color: #6b7280;
              background: rgba(107, 116, 128, 0.1);

              i {
                color: #6b7280;
              }
            }

            &.time-label {
              color: #9ca3af;
              background: rgba(156, 163, 175, 0.1);

              i {
                color: #9ca3af;
              }
            }
          }
        }
      }
    }

    .rank-trend {
      flex-shrink: 0;
      margin-left: 8px;

      .trend-0 {
        color: #ff4757;
        font-size: 18px;
        animation: pulse 2s infinite;
      }

      .trend-1,
      .trend-2 {
        color: #2ed573;
        font-size: 16px;
      }
    }
  }
}

.rank-footer {
  padding: 14px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;

  ::v-deep .el-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: #fff;
    padding: 8px 24px;
    font-weight: 500;
    transition: all 0.3s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }

    i {
      margin-right: 5px;
    }
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

@keyframes pulse {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}
</style>
